import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Fact,
  Facts,
  Icon,
  SearchSelect,
  type IconName,
} from '@evogard/ui';
import {
  ASSISTANCE_SERVICES,
  DISPATCH_PROGRESS_LABELS,
  DISPATCH_PROGRESS_STEPS,
  DISPATCH_STEP_LABELS,
  SERVICE_VARIANT_FIELD,
  SERVICE_VARIANT_LABELS,
  formatPhone,
  fromColumn,
  serviceVariantsOf,
  towBreakdown,
  type DispatchStepKind,
  type ServiceVariant,
} from '@evogard/domain';
import type { DispatchDetail } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  ASSISTANCE_KEY,
  PAYMENT_LABEL,
  SERVICE_ICON,
  km,
  minutes,
  reais,
  situationOf,
  when,
} from './assistance.js';
import { stepDetail } from './ProgressPanel.js';
import { TowBreakdownLines } from './TowBreakdown.js';

/* ------------------------------------------------------------------------- *
 * A linha do tempo com ícones — a do prestador e a da central
 * ------------------------------------------------------------------------- */

/**
 * A cor do marco, pela natureza do que aconteceu — as mesmas do `Badge`.
 * `concluido` é a etapa do roteiro já feita: o check cheio em verde, o mesmo
 * marcador do `Checklist`.
 */
type EventTone = 'neutro' | 'progresso' | 'sucesso' | 'alerta' | 'erro' | 'concluido';

// @valor
const TONE_DOT: Record<EventTone, string> = {
  neutro: 'bg-superficie-suave text-texto-suave',
  progresso: 'bg-progresso-suave text-progresso',
  sucesso: 'bg-sucesso-suave text-sucesso',
  alerta: 'bg-alerta-suave text-alerta',
  erro: 'bg-erro-suave text-erro',
  concluido: 'bg-sucesso text-texto-inverso',
};

export interface TimelineEntry {
  key: string;
  at: string | null;
  icon: IconName;
  tone: EventTone;
  title: string;
  detail?: ReactNode;
  actor?: string | null;
  /** O passo do roteiro que ainda não aconteceu, ou o fato miúdo (abriu, saiu da tela). */
  muted?: boolean;
}

/**
 * Uma linha do tempo vertical, em ordem de acontecimento: o ícone diz o tipo,
 * a cor diz se foi bom, ruim ou só informação, e a hora fica na coluna da
 * esquerda para o olho correr o cronograma.
 */
export function Timeline({ items, label }: { items: TimelineEntry[]; label: string }) {
  return (
    <ol aria-label={label} className="flex flex-col">
      {items.map((item, i) => (
        <li key={item.key} className="flex gap-3">
          <span className="w-24 shrink-0 pt-1.5 text-right text-xs tabular-nums text-texto-tenue">
            {item.at ? when(item.at) : 'a fazer'}
          </span>
          <span className="flex flex-col items-center">
            <span
              className={`flex size-8 shrink-0 items-center justify-center rounded-full ${
                item.muted && !item.at
                  ? 'border border-dashed border-borda text-texto-tenue'
                  : TONE_DOT[item.tone]
              }`}
            >
              <Icon nome={item.icon} size={16} />
            </span>
            {i < items.length - 1 && (
              <span aria-hidden="true" className="w-px flex-1 bg-borda" />
            )}
          </span>
          <span
            className={`flex min-w-0 flex-col gap-0.5 pb-4 pt-1 text-sm ${item.muted ? 'text-texto-tenue' : ''}`}
          >
            <span className={item.muted ? '' : 'font-semibold'}>{item.title}</span>
            {item.detail && <span className="text-texto-suave">{item.detail}</span>}
            {item.actor && (
              <span className="text-xs text-texto-tenue">por {item.actor}</span>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

// @valor
const STEP_LOOK: Record<
  DispatchStepKind,
  { icon: IconName; tone: EventTone; muted?: boolean }
> = {
  abriu: { icon: 'olho', tone: 'neutro', muted: true },
  saiu: { icon: 'mapa', tone: 'progresso' },
  chegou: { icon: 'ponto', tone: 'progresso' },
  placa_conferida: { icon: 'confirmado', tone: 'sucesso' },
  placa_errada: { icon: 'atencao', tone: 'erro' },
  placa_destravada: { icon: 'chave', tone: 'alerta' },
  iniciou: { icon: 'tocar', tone: 'progresso' },
  embarcou: { icon: 'veiculo', tone: 'progresso' },
  chegou_destino: { icon: 'bandeira', tone: 'progresso' },
  finalizou: { icon: 'confirmado', tone: 'sucesso' },
  fora_da_tela: { icon: 'pausa', tone: 'neutro', muted: true },
  voltou_a_tela: { icon: 'olho', tone: 'neutro', muted: true },
};

// @valor
const FILE_LABEL: Record<string, string> = {
  origem: 'Foto da chegada',
  destino: 'Foto da entrega',
  odometro: 'Foto do odômetro',
  outra: 'Outra foto',
};

/**
 * Os fatos do link que não entram na linha do tempo: abrir a página e sair ou
 * voltar à tela repetem a cada desbloqueio do celular e enterravam os marcos
 * (pedido de 30/10/2026). Continuam gravados — o "abriu" é o que separa "não
 * abriu" de "link aberto" no andamento —, só não viram linha.
 */
const HIDDEN_STEPS: ReadonlySet<DispatchStepKind> = new Set([
  'abriu',
  'fora_da_tela',
  'voltou_a_tela',
]);

const ROUTE_STEPS: ReadonlySet<DispatchStepKind> = new Set(DISPATCH_PROGRESS_STEPS);

/**
 * O que o PRESTADOR fez, pelo link: os marcos do roteiro, as fotos e o km —
 * na ordem em que aconteceram —, e os passos que ainda faltam no fim, apagados.
 * A etapa do roteiro feita vira o check verde; o que não é etapa (a placa
 * errada, o desbloqueio) mantém o ícone e a cor do que foi. O desbloqueio da
 * placa entra aqui, e não na central, porque é a resposta a um passo dele.
 */
export function providerTimeline(d: DispatchDetail): TimelineEntry[] {
  const p = d.progress;
  const done: TimelineEntry[] = [
    ...(p?.steps ?? [])
      .filter((s) => !HIDDEN_STEPS.has(s.kind))
      .map((s): TimelineEntry => {
        const look = STEP_LOOK[s.kind];
        const detail = stepDetail(s);
        const route = ROUTE_STEPS.has(s.kind);
        return {
          key: `passo-${s.id}`,
          at: s.at,
          icon: route ? 'confirmado' : look.icon,
          tone: detail.far ? 'alerta' : route ? 'concluido' : look.tone,
          title: DISPATCH_STEP_LABELS[s.kind],
          detail: detail.text ? (
            <span className={detail.far ? 'font-semibold text-erro' : ''}>
              {detail.text}
            </span>
          ) : undefined,
          muted: look.muted,
        };
      }),
    ...d.fileList.map((f): TimelineEntry => ({
      key: `foto-${f.id}`,
      at: f.takenAt ?? f.createdAt,
      icon: 'camera',
      tone: 'neutro',
      title: FILE_LABEL[f.kind] ?? 'Foto',
      detail:
        f.accuracyM !== null ? `com localização (±${f.accuracyM} m)` : 'sem localização',
    })),
    ...d.history
      .filter((h) => h.type === 'km_informado')
      .map((h): TimelineEntry => ({
        key: `km-${h.at}`,
        at: h.at,
        icon: 'calculadora',
        tone: 'neutro',
        title: 'Informou o km',
        detail:
          typeof h.data['km'] === 'number' ? `${h.data['km']} km rodados` : undefined,
      })),
  ].sort((a, b) => Date.parse(a.at!) - Date.parse(b.at!));

  const seen = new Set(p?.steps.map((s) => s.kind));
  const pending = (p?.route ?? [])
    .filter((step) => !seen.has(step))
    .map((step): TimelineEntry => ({
      key: `falta-${step}`,
      at: null,
      icon: STEP_LOOK[step].icon,
      tone: 'neutro',
      title: DISPATCH_STEP_LABELS[step],
      muted: true,
    }));
  return [...done, ...pending];
}

/** Os eventos do prestador que a central não repete na dela. */
const PROVIDER_EVENTS: ReadonlySet<string> = new Set([
  'foto_recebida',
  'km_informado',
  'placa_destravada',
]);

// @valor
const CENTRAL_LOOK: Record<string, { icon: IconName; tone: EventTone; title: string }> = {
  acionado: { icon: 'assistencia', tone: 'sucesso', title: 'Acionado' },
  agendado: { icon: 'calendario', tone: 'progresso', title: 'Agendado' },
  remarcado: { icon: 'calendario', tone: 'progresso', title: 'Remarcado' },
  agendamento_editado: {
    icon: 'documento',
    tone: 'neutro',
    title: 'Agendamento editado',
  },
  executado: { icon: 'confirmado', tone: 'sucesso', title: 'Executado' },
  cancelado: { icon: 'bloqueado', tone: 'erro', title: 'Cancelado' },
  link_reenviado: {
    icon: 'atualizar',
    tone: 'alerta',
    title: 'Link do prestador trocado',
  },
  script_copiado: { icon: 'copiar', tone: 'neutro', title: 'Script copiado' },
  pago: { icon: 'dinheiro', tone: 'sucesso', title: 'Pago' },
  cota_liberada: { icon: 'chave', tone: 'alerta', title: 'Liberado acima da cota' },
  continuacao_do_evento: {
    icon: 'veiculo',
    tone: 'neutro',
    title: 'Segundo reboque do mesmo evento',
  },
};

/** O que explica a liberação e a continuação, ao lado do motivo. */
function usageDetail(type: string, data: Record<string, unknown>): string | null {
  if (type === 'continuacao_do_evento' && typeof data['continues'] === 'string') {
    return `Continuação do ${data['continues']}. Não gastou cota.`;
  }
  if (type !== 'cota_liberada') return null;
  const codes = Array.isArray(data['usedCodes']) ? data['usedCodes'].join(', ') : '';
  return `Cota ${String(data['maxUses'])}, uso ${String(data['used'])}${codes && ` (${codes})`}.`;
}

/** O que a CENTRAL fez com o acionamento, do mais antigo ao mais novo. */
export function centralTimeline(d: DispatchDetail): TimelineEntry[] {
  return d.history
    .filter((h) => !PROVIDER_EVENTS.has(h.type))
    .map((h, i): TimelineEntry => {
      const look = CENTRAL_LOOK[h.type] ?? {
        icon: 'historico' as const,
        tone: 'neutro' as const,
        title: h.type,
      };
      const reason = typeof h.data['reason'] === 'string' ? h.data['reason'] : null;
      const usage = usageDetail(h.type, h.data);
      return {
        key: `${h.at}-${h.type}-${i}`,
        at: h.at,
        icon: look.icon,
        tone: look.tone,
        title: look.title,
        detail: [reason, usage].filter(Boolean).join(' ') || undefined,
        actor: h.actorName,
      };
    })
    .sort((a, b) => Date.parse(a.at!) - Date.parse(b.at!));
}

/* ------------------------------------------------------------------------- *
 * O resumo do topo e as seções
 * ------------------------------------------------------------------------- */

function SummaryItem({
  icon,
  label,
  children,
}: {
  icon: IconName;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-superficie-suave text-texto-suave">
        <Icon nome={icon} size={20} />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-xs text-texto-suave">{label}</span>
        <span className="truncate text-sm font-semibold">{children}</span>
      </span>
    </div>
  );
}

/** A faixa de cima: o que quem abre a mesa quer saber antes de escolher a aba. */
export function DeskSummary({ d }: { d: DispatchDetail }) {
  const service = ASSISTANCE_SERVICES.find((s) => s.type === d.serviceType);
  const situation = situationOf(d);
  return (
    <section
      aria-label="Resumo do acionamento"
      className="grid grid-cols-1 gap-3 rounded-lg border border-borda p-3 sm:grid-cols-2 lg:grid-cols-5"
    >
      <SummaryItem icon={SERVICE_ICON[d.serviceType]} label="Serviço">
        {service?.label ?? '—'}
        {d.serviceVariant ? ` · ${SERVICE_VARIANT_LABELS[d.serviceVariant]}` : ''}
        {d.towKind ? ` · ${d.towKind === 'comum' ? 'comum' : 'emergencial'}` : ''}
      </SummaryItem>
      <SummaryItem icon="etiqueta" label="Status">
        <Badge tone={situation.tone}>{situation.label}</Badge>
      </SummaryItem>
      <SummaryItem icon="mapa" label="Andamento">
        {d.progress ? DISPATCH_PROGRESS_LABELS[d.progress.progress] : '—'}
      </SummaryItem>
      <SummaryItem icon="assistencia" label="Prestador">
        {d.provider.name}
      </SummaryItem>
      <SummaryItem icon="dinheiro" label="A Evogard paga">
        {d.amount === null ? 'A combinar · valor em Pagamentos' : reais(d.amount)}
        {d.settlement ? ' · pago' : ''}
      </SummaryItem>
    </section>
  );
}

/** Um bloco da aba Resumo: o ícone e o título, e os dados embaixo. */
function Block({
  icon,
  title,
  children,
}: {
  icon: IconName;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-borda p-3">
      <h3 className="flex items-center gap-2 text-sm font-bold">
        <span className="flex size-7 items-center justify-center rounded-md bg-superficie-suave text-texto-suave">
          <Icon nome={icon} size={16} />
        </span>
        {title}
      </h3>
      <Facts>{children}</Facts>
    </section>
  );
}

/** A aba Resumo: o acionamento em quatro blocos, cada dado com o ícone dele. */
/**
 * O motivo do reboque ou o serviço residencial (30/10/2026). O acionamento que
 * nasceu antes do campo chega sem ele, e sem ele o pagamento recusa — quem
 * aciona preenche aqui, enquanto não houver pagamento.
 */
function VariantFact({ d }: { d: DispatchDetail }) {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const [value, setValue] = useState('');
  const variants = serviceVariantsOf(d.serviceType);
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.setServiceVariant({
          params: { id: d.id },
          body: { serviceVariant: value as ServiceVariant },
        }),
        200,
        'Não foi possível salvar.',
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: ASSISTANCE_KEY }),
  });
  if (variants === null) return null;
  const label =
    SERVICE_VARIANT_FIELD[d.serviceType as keyof typeof SERVICE_VARIANT_FIELD];
  if (d.serviceVariant)
    return (
      <Fact label={label} icon="etiqueta">
        {SERVICE_VARIANT_LABELS[d.serviceVariant]}
      </Fact>
    );
  if (d.settlement || !can(context, 'assistance.dispatches', 'criar'))
    return (
      <Fact label={label} icon="etiqueta">
        Não informado
      </Fact>
    );
  return (
    <div className="grid gap-2 sm:col-span-2">
      <Alert tone="alerta">
        Este acionamento nasceu sem o {label.toLowerCase()}, e sem ele o pagamento não
        sai: é o que escolhe a categoria no Conta Azul.
      </Alert>
      {save.error && <Alert tone="erro">{save.error.message}</Alert>}
      <div className="flex flex-wrap items-end gap-3">
        <SearchSelect
          label={label}
          required
          options={variants.map((v) => ({ value: v, label: SERVICE_VARIANT_LABELS[v] }))}
          value={value}
          onChange={setValue}
        />
        <Button
          size="sm"
          disabled={!value || save.isPending}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Salvar
        </Button>
      </div>
    </div>
  );
}

export function OverviewTab({ d }: { d: DispatchDetail }) {
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Block icon="pessoa" title="Associado e veículo">
        <Fact label="Associado" icon="pessoa">
          {d.memberName ?? '—'}
        </Fact>
        <Fact label="WhatsApp de retorno" icon="telefone">
          {formatPhone(d.callbackPhone)}
        </Fact>
        <Fact label="Placa" icon="etiqueta">
          {d.plate ?? '—'}
        </Fact>
        <Fact label="Veículo" icon="veiculo">
          {d.vehicleLabel}
        </Fact>
        {d.vehicleKind && (
          <Fact label="Tipo de veículo" icon="veiculo">
            {d.vehicleKind}
          </Fact>
        )}
      </Block>
      <Block icon="mapa" title="Onde">
        <Fact label="Onde está o veículo" icon="ponto" wide>
          {d.origin.label}
        </Fact>
        {d.destination && (
          <Fact label="Para onde vai" icon="bandeira" wide>
            {d.destination.label}
          </Fact>
        )}
        {d.towKind && (
          <Fact label="Tipo de reboque" icon="veiculo">
            {d.towKind === 'comum' ? 'Comum' : 'Emergencial'}
          </Fact>
        )}
        <VariantFact d={d} />
      </Block>
      <Block icon="documento" title="Ocorrência">
        <Fact label="O que aconteceu" icon="documento" wide>
          {d.description}
        </Fact>
        {d.providerNotes && (
          <Fact label="Observação para o prestador" icon="informacao" wide>
            {d.providerNotes}
          </Fact>
        )}
        <Fact label="Protocolo OPA" icon="etiqueta">
          {d.opaSuiteProtocol}
        </Fact>
        {d.scheduledFor && (
          <Fact label="Marcado para" icon="calendario">
            {when(d.scheduledFor)}
          </Fact>
        )}
        {d.cancelReason && (
          <Fact label="Motivo do cancelamento" icon="bloqueado" wide>
            {d.cancelReason}
          </Fact>
        )}
      </Block>
      <Block icon="assistencia" title="Prestador e pagamento">
        <Fact label="Prestador" icon="assistencia">
          {d.provider.name}
        </Fact>
        <Fact label="WhatsApp do prestador" icon="telefone">
          {d.provider.whatsapp ? (
            <a
              className="tabular-nums underline"
              href={`https://wa.me/55${d.provider.whatsapp}`}
              target="_blank"
              rel="noreferrer"
            >
              {formatPhone(d.provider.whatsapp)}
            </a>
          ) : (
            'Sem WhatsApp no cadastro da empresa'
          )}
        </Fact>
        <Fact label="Pagamento" icon="dinheiro">
          {PAYMENT_LABEL[d.paymentMode]}
        </Fact>
        <Fact label="Serviço" icon="etiqueta">
          {d.addon ? `${d.serviceName} + ${d.addon.name}` : d.serviceName}
        </Fact>
        <Fact label="A Evogard paga" icon="dinheiro">
          {d.amount === null ? 'A combinar' : reais(d.amount)}
          {/* O valor do adicional é o da tabela no dia; no combinado, o total já o inclui. */}
          {d.addon && d.valuationSource === 'tabela' && (
            <span className="block text-xs text-texto-suave">
              Inclui {d.addon.name}: {reais(d.addon.amount)}
            </span>
          )}
        </Fact>
        {d.associateExcessAmount !== '0.00' && (
          <Fact label="O associado paga ao prestador" icon="pessoa">
            {reais(d.associateExcessAmount)}
          </Fact>
        )}
      </Block>
    </div>
  );
}

/**
 * A conta do reboque aberta, com os números gravados no dia (02/11/2026). Só no
 * valor da tabela de saída + km: o combinado e o fixo não têm km na conta.
 */
function openTowAccount(d: DispatchDetail) {
  if (d.valuationSource !== 'tabela' || d.price?.pricing !== 'saida_e_km') return null;
  const flatAmount = fromColumn(d.price.flatAmount);
  const perKmAmount = fromColumn(d.price.perKmAmount);
  const amount = fromColumn(d.amount);
  if (
    d.countedM === null ||
    d.price.includedM === null ||
    flatAmount === null ||
    perKmAmount === null ||
    amount === null
  )
    return null;
  const addonAmount = d.addon ? fromColumn(d.addon.amount) : null;
  return {
    breakdown: towBreakdown(
      { pricing: 'saida_e_km', flatAmount, includedM: d.price.includedM, perKmAmount },
      d.countedM,
      d.planCoverageM,
    ),
    addon:
      d.addon && addonAmount !== null
        ? { name: d.addon.name, amount: addonAmount }
        : null,
    evogardTotal: amount,
  };
}

/** A conta, ao lado do mapa na aba Rota e valor. */
export function AccountBlock({ d }: { d: DispatchDetail }) {
  const open = openTowAccount(d);
  return (
    <Block icon="calculadora" title="A conta">
      {d.approach && (
        <Fact label="Ida até o associado" icon="mapa">
          {km(d.approach.m)} · {minutes(d.approach.s)}
        </Fact>
      )}
      {d.tow && (
        <Fact label="Reboque até o destino" icon="veiculo">
          {km(d.tow.m)} · {minutes(d.tow.s)}
        </Fact>
      )}
      {d.back && (
        <Fact label="Volta à base" icon="garagem">
          {km(d.back.m)} · {minutes(d.back.s)}
        </Fact>
      )}
      {open && (
        <Fact label="Conta do reboque" icon="calculadora" wide>
          <TowBreakdownLines
            {...open}
            footer={
              <p className="text-xs font-semibold">
                {d.memberExcessAck
                  ? `Associado ciente pelo link em ${when(d.memberExcessAck.at)} (${reais(d.memberExcessAck.amount)}).`
                  : 'O associado ainda não confirmou pelo link que está ciente.'}
              </p>
            }
          />
        </Fact>
      )}
      {d.price && !open && (
        <Fact label="Tabela" icon="etiqueta" wide>
          {d.price.pricing === 'a_combinar'
            ? 'A combinar: o valor é informado depois do serviço'
            : d.price.pricing === 'fixo'
              ? `Preço fixo de ${reais(d.price.flatAmount)}`
              : `Saída ${reais(d.price.flatAmount)} até ${km(d.price.includedM)}, ${reais(d.price.perKmAmount)}/km depois · cobertura ${d.planCoverageM === null ? 'sem limite' : km(d.planCoverageM)}`}
        </Fact>
      )}
      {!open && (
        <Fact label="A Evogard paga" icon="dinheiro">
          {d.amount === null ? 'A combinar · informe em Pagamentos' : reais(d.amount)}
          {d.valuationSource === 'informado'
            ? ' · combinado'
            : d.valuationSource === 'cancelamento'
              ? ' · do cancelamento'
              : ''}
        </Fact>
      )}
      {!open && d.associateExcessAmount !== '0.00' && (
        <Fact label="O associado paga" icon="pessoa">
          {reais(d.associateExcessAmount)}
        </Fact>
      )}
      {d.providerReportedM !== null && (
        <Fact label="Km do prestador" icon="calculadora">
          {km(d.providerReportedM)}
        </Fact>
      )}
    </Block>
  );
}

/** As fotos do prestador, com quando e onde, e o link de onde elas vêm. */
export function PhotosTab({
  d,
  onOpen,
  opening,
}: {
  d: DispatchDetail;
  onOpen: (fileId: number) => void;
  opening: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      {d.fileList.length === 0 ? (
        <EmptyState icon="camera" title="Nenhuma foto ainda">
          {d.executedAt
            ? 'O prestador manda pelo link do acionamento.'
            : 'O link do prestador nasce quando o agendamento for executado.'}
        </EmptyState>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {d.fileList.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-3 rounded-lg border border-borda p-3 text-sm"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-superficie-suave text-texto-suave">
                <Icon nome="camera" size={20} />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="font-semibold">{FILE_LABEL[f.kind] ?? 'Foto'}</span>
                <span className="text-xs text-texto-suave">
                  {when(f.takenAt)} ·{' '}
                  {f.accuracyM !== null
                    ? `localização ±${f.accuracyM} m`
                    : 'sem localização'}
                </span>
              </span>
              <Button
                size="sm"
                variant="discreto"
                disabled={opening}
                onClick={() => onOpen(f.id)}
              >
                Abrir
              </Button>
            </li>
          ))}
        </ul>
      )}
      {d.linkUrl && (
        <p className="flex items-start gap-2 text-xs break-all text-texto-tenue">
          <Icon nome="clipe" size={16} className="shrink-0" />
          {d.linkUrl} (vale até {when(d.linkExpiresAt)})
        </p>
      )}
    </div>
  );
}
